import { useState, useEffect } from 'react';
import { apiService } from '../../services/api.service';
import { useAppStore } from '../../store/appStore';
import './CaregiverDashboard.css';

interface MonitoredUser {
  id: string;
  name: string;
  email: string;
  lastActive: number;
  status: 'active' | 'inactive' | 'emergency';
  currentGesture?: string;
  location?: string;
}

interface ActivityLog {
  id: string;
  userId: string;
  userName: string;
  eventType: string;
  eventData: any;
  timestamp: number;
}

export const CaregiverDashboard = () => {
  const { user } = useAppStore();
  const [monitoredUsers, setMonitoredUsers] = useState<MonitoredUser[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadDashboardData();
      const interval = setInterval(loadDashboardData, 10000); // Refresh every 10s
      return () => clearInterval(interval);
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      // In a real app, this would fetch from API
      // For now, we'll use mock data
      const mockUsers: MonitoredUser[] = [
        {
          id: '1',
          name: 'John Doe',
          email: 'john@example.com',
          lastActive: Date.now() - 120000,
          status: 'active',
          currentGesture: 'wave'
        },
        {
          id: '2',
          name: 'Jane Smith',
          email: 'jane@example.com',
          lastActive: Date.now() - 300000,
          status: 'inactive'
        }
      ];

      setMonitoredUsers(mockUsers);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      setLoading(false);
    }
  };

  const loadUserActivity = async (userId: string) => {
    try {
      const stats = await apiService.getStats() as any;
      // Filter logs for selected user
      setActivityLogs(stats.recentActivity || []);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUser(userId);
    loadUserActivity(userId);
  };

  const formatTime = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'inactive': return '#6c757d';
      case 'emergency': return '#dc3545';
      default: return '#6c757d';
    }
  };

  if (!user) {
    return (
      <div className="caregiver-dashboard">
        <div className="dashboard-error">
          <h2>Login Required</h2>
          <p>Please login to access the caregiver dashboard</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="caregiver-dashboard">
        <div className="dashboard-loading">Loading dashboard...</div>
      </div>
    );
  }

  const selectedUserData = monitoredUsers.find(u => u.id === selectedUser);

  return (
    <div className="caregiver-dashboard">
      <div className="dashboard-header">
        <h2>👨‍⚕️ Caregiver Dashboard</h2>
        <p>Monitor and support users in real-time</p>
      </div>

      <div className="dashboard-settings">
        <label>
          <input
            type="checkbox"
            checked={emailAlerts}
            onChange={(e) => setEmailAlerts(e.target.checked)}
          />
          Enable email alerts for emergencies
        </label>
      </div>

      <div className="dashboard-content">
        <div className="users-panel">
          <h3>Monitored Users ({monitoredUsers.length})</h3>
          <div className="users-grid">
            {monitoredUsers.map(user => (
              <div
                key={user.id}
                className={`user-card ${selectedUser === user.id ? 'selected' : ''}`}
                onClick={() => handleUserSelect(user.id)}
              >
                <div className="user-card-header">
                  <div
                    className="status-indicator"
                    style={{ backgroundColor: getStatusColor(user.status) }}
                  />
                  <h4>{user.name}</h4>
                </div>
                <p className="user-email">{user.email}</p>
                <div className="user-meta">
                  <span className="last-active">
                    Last active: {formatTime(user.lastActive)}
                  </span>
                  {user.currentGesture && (
                    <span className="current-gesture">
                      Current: {user.currentGesture}
                    </span>
                  )}
                </div>
                {user.status === 'emergency' && (
                  <div className="emergency-badge">
                    🚨 EMERGENCY
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {selectedUserData && (
          <div className="user-details">
            <div className="details-header">
              <h3>{selectedUserData.name}</h3>
              <span className="status-badge" style={{ backgroundColor: getStatusColor(selectedUserData.status) }}>
                {selectedUserData.status}
              </span>
            </div>

            <div className="details-stats">
              <div className="stat-card">
                <h4>Activity Status</h4>
                <p className="stat-value">{selectedUserData.status}</p>
              </div>
              <div className="stat-card">
                <h4>Last Active</h4>
                <p className="stat-value">{formatTime(selectedUserData.lastActive)}</p>
              </div>
              <div className="stat-card">
                <h4>Current Gesture</h4>
                <p className="stat-value">{selectedUserData.currentGesture || 'None'}</p>
              </div>
            </div>

            <div className="activity-log">
              <h4>Activity Log</h4>
              {activityLogs.length === 0 ? (
                <p className="empty-state">No recent activity</p>
              ) : (
                <div className="log-list">
                  {activityLogs.slice(0, 20).map(log => (
                    <div key={log.id} className="log-item">
                      <span className="log-type">{log.eventType}</span>
                      <span className="log-time">{formatTime(log.timestamp)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="quick-actions">
              <h4>Quick Actions</h4>
              <button className="btn btn-primary">
                📞 Call User
              </button>
              <button className="btn btn-secondary">
                📧 Send Email
              </button>
              <button className="btn btn-warning">
                🚨 Check Status
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
